import React, { useEffect, useRef, useState } from 'react';

import { loadGoogleMaps } from '@/lib/googleMaps';
import { GSM_MAP_STYLE } from '@/lib/googleMapsStyle';
import {
  WORKFLOW_STOP_TYPE_LABELS,
  WorkflowStopType,
} from '@/services/api/stopWorkflows';

import type { FieldDraft, MilestoneDraft } from './WorkflowBuilderModal';

interface WorkflowPhonePreviewProps {
  milestones: MilestoneDraft[];
  clientName?: string;
  stopType: WorkflowStopType;
}

/** How many photo tiles to draw before collapsing into a "+N" tile. */
const MAX_PHOTO_TILES = 5;

/** Thumb width (h-7 w-7) + track padding (px-1 → 4px each side). */
const THUMB_PX = 28;
const TRACK_PAD_PX = 8;
/** Fraction of the track the thumb must travel to count as confirmed. */
const CONFIRM_THRESHOLD = 0.8;

/**
 * Decorative sample route for the map behind the bottom sheet (South
 * Jakarta, same canvas as the dispatch map). The stop marker is the last
 * point — the geography is illustrative; real stops are only known at
 * route-creation time.
 */
const SAMPLE_ROUTE = [
  { lat: -6.2607, lng: 106.8105 },
  { lat: -6.2529, lng: 106.8158 },
  { lat: -6.2447, lng: 106.8221 },
  { lat: -6.2411, lng: 106.8305 },
  { lat: -6.2346, lng: 106.8331 },
];

/** Schematic stand-in when the Google Maps SDK is unavailable (no key). */
const MapFallback: React.FC = () => (
  <div
    className="absolute inset-0 bg-bg-weak-50"
    style={{
      backgroundImage:
        'linear-gradient(var(--color-rule) 1px, transparent 1px), linear-gradient(90deg, var(--color-rule) 1px, transparent 1px)',
      backgroundSize: '28px 28px',
    }}
  >
    <svg
      className="absolute inset-0 h-full w-full"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
    >
      <path
        d="M 18 96 C 34 74, 44 60, 62 34"
        fill="none"
        stroke="var(--color-accent, #5E2AAC)"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
    <span className="absolute left-[58%] top-[30%] block h-3 w-3 rounded-full border-2 border-white bg-green-600 shadow-regular-xs" />
  </div>
);

const PhotoTile: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex aspect-square flex-col items-center justify-center gap-0.5 rounded-gsm border border-dashed border-rule-strong bg-tint text-text-soft-400">
    <i className="ri-camera-line text-[15px]" />
    <span className="text-[8px] font-medium">{label}</span>
  </div>
);

/**
 * The rider app's signature placeholder (SignatureCanvas before the recipient
 * starts): fixed white pad with dark ink copy in both themes, as on-device.
 */
const SignaturePad: React.FC = () => (
  <div
    data-testid="preview-signature-pad"
    className="flex h-16 flex-col items-center justify-center gap-0.5 rounded-gsm border border-rule bg-static-white px-2 text-center text-static-black"
  >
    <i aria-hidden className="ri-quill-pen-line text-[13px] opacity-60" />
    <span className="text-[9px] font-semibold">Tanda tangan di sini</span>
    <span className="text-[8px] opacity-60">
      Serahkan HP ke penerima untuk tanda tangan
    </span>
  </div>
);

const FieldPreview: React.FC<{ field: FieldDraft; index: number }> = ({
  field,
  index,
}) => {
  const label = field.label.trim() || `Field ${index + 1}`;

  return (
    <div>
      <p className="mb-1 text-[10px] font-semibold text-text-strong-950">
        {label}
        {field.required && <span className="ml-0.5 text-red-500">*</span>}
      </p>

      {field.type === 'IMAGE' &&
        (() => {
          const exact = field.imageMode === 'exact';
          const count = exact ? Math.max(1, Number(field.imageCount) || 1) : 1;
          const shown = Math.min(count, MAX_PHOTO_TILES);
          const overflow = count - shown;
          return (
            <>
              <div className="grid grid-cols-3 gap-1.5">
                {Array.from({ length: shown }, (_, i) => (
                  <PhotoTile key={i} label={exact ? `Photo ${i + 1}` : 'Add'} />
                ))}
                {overflow > 0 && (
                  <div className="flex aspect-square items-center justify-center rounded-gsm border border-dashed border-rule-strong bg-tint font-mono text-[10px] font-semibold tabular-nums text-text-sub-600">
                    +{overflow}
                  </div>
                )}
              </div>
              <p className="mt-1 text-[8px] text-text-soft-400">
                {exact
                  ? `Exactly ${count} photo${count > 1 ? 's' : ''}`
                  : 'At least 1 photo'}
              </p>
            </>
          );
        })()}

      {field.type === 'SIGNATURE' && <SignaturePad />}

      {field.type === 'TEXT' && (
        <>
          <div className="flex h-8 items-center rounded-gsm border border-rule bg-bg-white-0 px-2 text-[9px] text-text-soft-400">
            Type here…
          </div>
          {field.maxLength.trim() !== '' && (
            <p className="mt-1 text-[8px] text-text-soft-400">
              Max {field.maxLength} characters
            </p>
          )}
        </>
      )}

      {field.type === 'NUMBER' && (
        <>
          <div className="flex h-8 items-center justify-between rounded-gsm border border-rule bg-bg-white-0 px-2 font-mono text-[9px] tabular-nums text-text-soft-400">
            0
            <i className="ri-hashtag text-[10px]" />
          </div>
          {(field.min.trim() !== '' || field.max.trim() !== '') && (
            <p className="mt-1 text-[8px] text-text-soft-400">
              {field.min.trim() !== '' && field.max.trim() !== ''
                ? `Between ${field.min} and ${field.max}`
                : field.min.trim() !== ''
                  ? `Min ${field.min}`
                  : `Max ${field.max}`}
            </p>
          )}
        </>
      )}
    </div>
  );
};

/**
 * Illustrative mock of the rider m-app stop screen. Milestones render as a
 * vertical stepper-accordion — the active step is expanded, earlier steps are
 * completed, later ones locked. Slide the bottom bar (drag the thumb to the
 * end) to confirm the active step, exactly like the rider does on-device;
 * tapping any step header jumps the simulation there.
 */
const WorkflowPhonePreview: React.FC<WorkflowPhonePreviewProps> = ({
  milestones,
  clientName,
  stopType,
}) => {
  /** 0..total-1 = active step; total = every milestone confirmed. */
  const [activeIndex, setActiveIndex] = useState(0);

  const total = milestones.length;
  const allDone = total > 0 && activeIndex >= total;

  // Keep the simulated position valid as milestones are added/removed.
  useEffect(() => {
    if (activeIndex > total) setActiveIndex(total);
  }, [total, activeIndex]);

  // ── Slide-to-confirm drag state ─────────────────────────────────────
  const trackRef = useRef<HTMLDivElement | null>(null);
  const dragOriginX = useRef(0);
  const dragXRef = useRef(0);
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);

  const canSlide = total > 0 && !allDone;

  // ── Map behind the bottom sheet (same SDK + style as DispatchMap) ───
  const mapEl = useRef<HTMLDivElement | null>(null);
  const [mapFailed, setMapFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then((maps: any) => {
        if (cancelled || !mapEl.current) return;
        const map = new maps.Map(mapEl.current, {
          center: SAMPLE_ROUTE[SAMPLE_ROUTE.length - 1],
          zoom: 13,
          disableDefaultUI: true,
          gestureHandling: 'none',
          clickableIcons: false,
          keyboardShortcuts: false,
          styles: GSM_MAP_STYLE,
          backgroundColor: '#f7f7f5',
        });
        new maps.Polyline({
          map,
          path: SAMPLE_ROUTE,
          strokeColor: '#5E2AAC',
          strokeOpacity: 0.85,
          strokeWeight: 3,
        });
        new maps.Marker({
          map,
          position: SAMPLE_ROUTE[SAMPLE_ROUTE.length - 1],
          icon: {
            path: maps.SymbolPath.CIRCLE,
            scale: 5,
            fillColor: '#16a34a',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
          },
        });
      })
      .catch(() => {
        if (!cancelled) setMapFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const maxDrag = (): number => {
    const track = trackRef.current;
    return track
      ? Math.max(0, track.clientWidth - TRACK_PAD_PX - THUMB_PX)
      : 0;
  };

  const setDrag = (value: number) => {
    dragXRef.current = value;
    setDragX(value);
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!canSlide) return;
    dragOriginX.current = e.clientX;
    setDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    const next = Math.min(
      Math.max(e.clientX - dragOriginX.current, 0),
      maxDrag()
    );
    setDrag(next);
  };

  const endDrag = () => {
    if (!dragging) return;
    setDragging(false);
    if (maxDrag() > 0 && dragXRef.current >= maxDrag() * CONFIRM_THRESHOLD) {
      setActiveIndex((i) => i + 1);
    }
    setDrag(0);
  };

  const slideProgress = maxDrag() > 0 ? dragX / maxDrag() : 0;

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-text-sub-600">
          Rider preview
        </p>
        <span className="font-mono text-[10px] tabular-nums text-text-soft-400">
          {Math.min(activeIndex + (allDone ? 0 : 1), Math.max(total, 1))}/
          {Math.max(total, 1)}
        </span>
      </div>

      {/* Phone frame */}
      <div className="rounded-[32px] bg-ink p-1.5 shadow-custom-shadows-large">
        <div className="relative flex h-[540px] flex-col overflow-hidden rounded-[26px] bg-bg-white-0">
          {/* Status bar + notch */}
          <div className="relative shrink-0 border-b border-rule px-4 pb-1 pt-2">
            <div className="absolute left-1/2 top-1.5 h-4 w-20 -translate-x-1/2 rounded-full bg-ink" />
            <div className="flex items-center justify-between font-mono text-[9px] tabular-nums text-text-strong-950">
              <span>09:41</span>
              <span className="flex items-center gap-1">
                <i className="ri-signal-wifi-fill text-[9px]" />
                <i className="ri-battery-fill text-[10px]" />
              </span>
            </div>
            {/* App bar */}
            <div className="flex items-center gap-2 pb-2 pt-2.5">
              <i className="ri-arrow-left-line text-[13px] text-text-strong-950" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[11px] font-bold leading-tight text-text-strong-950">
                  {clientName || 'Client name'}
                </p>
                <p className="text-[8px] text-text-sub-600">
                  {WORKFLOW_STOP_TYPE_LABELS[stopType]} stop · {total}{' '}
                  milestone{total === 1 ? '' : 's'}
                </p>
              </div>
            </div>
          </div>

          {/* Map + bottom sheet */}
          <div className="relative flex-1 overflow-hidden">
            <div ref={mapEl} className="absolute inset-0" />
            {mapFailed && <MapFallback />}

            {/* Bottom sheet */}
            <div className="absolute inset-x-0 bottom-0 flex max-h-[76%] flex-col rounded-t-gsm-md border-t border-rule bg-bg-white-0 shadow-[0_-6px_16px_rgba(23,23,23,0.10)]">
              <div className="mx-auto mt-1.5 h-1 w-9 shrink-0 rounded-full bg-rule-strong" />

              {/* Milestone stepper-accordion */}
              <div className="min-h-0 flex-1 overflow-y-auto px-3.5 pb-2 pt-2">
            {total === 0 && (
              <p className="px-2 py-6 text-center text-[10px] text-text-soft-400">
                Add a milestone to see the rider flow.
              </p>
            )}
            {milestones.map((milestone, index) => {
              const name = milestone.name.trim() || `Milestone ${index + 1}`;
              const state =
                index < activeIndex
                  ? 'done'
                  : index === activeIndex
                    ? 'active'
                    : 'locked';
              const expanded = state === 'active';
              const isLast = index === total - 1;
              const fieldCount = milestone.fields.length;

              return (
                <div key={milestone.uid} className="flex gap-2.5">
                  {/* Marker + connector */}
                  <div className="flex w-3 shrink-0 flex-col items-center">
                    {state === 'done' ? (
                      <span className="mt-0.5 flex h-3 w-3 shrink-0 items-center justify-center rounded-full bg-green-500">
                        <i className="ri-check-line text-[8px] leading-none text-white" />
                      </span>
                    ) : state === 'active' ? (
                      <span className="mt-0.5 h-3 w-3 shrink-0 rounded-full bg-accent" />
                    ) : (
                      <span className="mt-0.5 h-3 w-3 shrink-0 rounded-full border-[1.5px] border-rule-strong bg-bg-white-0" />
                    )}
                    {!isLast && <div className="my-1 w-px flex-1 bg-rule" />}
                  </div>

                  {/* Accordion item */}
                  <div className={`min-w-0 flex-1 ${isLast ? '' : 'pb-3.5'}`}>
                    <button
                      type="button"
                      onClick={() => setActiveIndex(index)}
                      className="flex w-full items-start gap-1 text-left"
                    >
                      <span className="min-w-0 flex-1">
                        <span
                          className={`block truncate text-[11px] font-bold leading-tight tracking-tight ${
                            state === 'locked'
                              ? 'text-text-soft-400'
                              : 'text-text-strong-950'
                          }`}
                        >
                          {name}
                        </span>
                        <span
                          className={`mt-0.5 block text-[8px] leading-tight ${
                            state === 'done'
                              ? 'font-mono tabular-nums text-text-sub-600'
                              : 'text-text-soft-400'
                          }`}
                        >
                          {state === 'done'
                            ? `Completed · 09:${String(12 + index).padStart(2, '0')}`
                            : fieldCount === 0
                              ? 'Confirmation only'
                              : `${fieldCount} field${fieldCount === 1 ? '' : 's'} to capture`}
                        </span>
                      </span>
                      <i
                        className={`ri-arrow-down-s-line mt-px shrink-0 text-[12px] text-text-soft-400 transition-transform duration-200 ${
                          expanded ? 'rotate-180' : ''
                        }`}
                      />
                    </button>

                    {/* Collapsible body */}
                    <div
                      className={`grid transition-[grid-template-rows] duration-200 ease-out ${
                        expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                      }`}
                    >
                      <div className="min-h-0 overflow-hidden">
                        {fieldCount === 0 ? (
                          <p className="pt-1.5 text-[9px] leading-relaxed text-text-sub-600">
                            No evidence needed — confirm you've completed this
                            checkpoint.
                          </p>
                        ) : (
                          <div className="space-y-2.5 pt-2">
                            {milestone.fields.map((field, fIndex) => (
                              <FieldPreview
                                key={field.uid}
                                field={field}
                                index={fIndex}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
              </div>

              {/* Sheet footer — slide the thumb to confirm the active step */}
              <div className="shrink-0 border-t border-rule px-2.5 pb-1.5 pt-2">
            <div
              ref={trackRef}
              className={`relative flex h-9 items-center overflow-hidden rounded-gsm px-1 ${
                total === 0
                  ? 'bg-tint-strong'
                  : allDone
                    ? 'bg-green-500'
                    : 'bg-ink'
              }`}
            >
              <div
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                style={{ transform: `translateX(${dragX}px)` }}
                className={`z-10 flex h-7 w-7 shrink-0 touch-none select-none items-center justify-center rounded-gsm-sm bg-bg-white-0 ${
                  canSlide ? 'cursor-grab active:cursor-grabbing' : ''
                } ${dragging ? '' : 'transition-transform duration-200 ease-out'}`}
              >
                <i
                  className={`text-[13px] ${
                    total === 0
                      ? 'ri-arrow-right-double-line text-text-soft-400'
                      : allDone
                        ? 'ri-check-line text-green-600'
                        : 'ri-arrow-right-double-line text-ink'
                  }`}
                />
              </div>
              <span
                style={{ opacity: 1 - slideProgress }}
                className={`min-w-0 flex-1 truncate px-1 text-center text-[9px] font-semibold tracking-wide ${
                  total === 0
                    ? 'text-text-soft-400'
                    : allDone
                      ? 'text-white'
                      : 'text-white/80'
                }`}
              >
                {total === 0
                  ? 'No milestones yet'
                  : allDone
                    ? 'Stop complete — all milestones confirmed'
                    : `Slide to confirm · ${
                        milestones[Math.min(activeIndex, total - 1)].name.trim() ||
                        `Milestone ${activeIndex + 1}`
                      }`}
              </span>
            </div>
                {/* Home indicator */}
                <div className="mt-1.5 flex justify-center">
                  <div className="h-1 w-16 rounded-full bg-ink/20" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p className="mt-2 text-[10px] leading-relaxed text-text-soft-400">
        Illustrative — the rider app renders the final UI. Tap a step to jump
        there, or drag the slider to confirm it.
      </p>
    </div>
  );
};

export default WorkflowPhonePreview;
